import type { ObjectId } from '@racemap/sdk/schema/base';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { RacemapGeoTypes } from '@racemap/utilities/types/geos';
import { Button, type ButtonProps, Space } from 'antd';
import type { CSSProperties, FC, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ParticipantTabKeys } from '../../EventEditor/ParticipantTab/ParticipantTab';
import type { PredictionTabKeys } from '../../EventEditor/PredictionTab/PredictionTab';
export { LatLngExternalLink } from './LatLngExternalLink';
import {
  IconBook,
  IconCalculator,
  IconChartLine,
  IconExternal,
  IconLeaderboard,
  IconLink,
  IconMap,
  IconMoney,
  IconPaintRoller,
  IconPrediction,
  IconRunner,
  IconStripe,
  IconTracker,
  IconUser,
  IconWallet,
} from '../../Icon';

export interface Props {
  link: string;
  label: ReactNode;
  title?: string;
  icon: React.ReactNode;
  as: 'button' | 'href' | 'icon';
  openInNewTab?: boolean;
  style?: CSSProperties;
  buttonType?: ButtonProps['type'];
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * ### Link Component
 * can be used as a button, a href or an icon
 * @param param0
 * @returns
 */
export const RawLink: FC<Props> = ({
  link,
  label,
  title,
  icon,
  as,
  style,
  buttonType = 'default',
  openInNewTab = false,
  onClick,
}) => {
  const isExternal = link.startsWith('http');
  const navigate = useNavigate();
  const href = as === 'href' || as === 'icon' || buttonType === 'link' ? link : undefined;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    onClick?.(event);
    event.preventDefault();

    if (isExternal || openInNewTab) {
      window.open(link, '_newtab');
    } else {
      navigate(link);
    }
  };

  switch (as) {
    case 'button':
      return (
        <Button type={buttonType} onClick={handleClick} title={title} style={style} href={href}>
          <Space size={5}>
            {icon}
            {label}
          </Space>
        </Button>
      );
    case 'href':
      return (
        <Button
          type="link"
          title={title}
          onClick={handleClick}
          href={href}
          style={{
            padding: '0 2px',
            color: RacemapColors.PaleBlue,
            height: '1rem',
            ...style,
          }}
        >
          <Space size={3} align="center">
            {icon}
            {label}
          </Space>
        </Button>
      );
    case 'icon':
      return (
        <Button
          type="link"
          title={`${label}: ${title}`}
          href={href}
          onClick={handleClick}
          style={{ padding: '0 2px', color: RacemapColors.PaleBlue, height: '1rem', ...style }}
        >
          {icon}
        </Button>
      );
  }
};

export const StripeLink: FC<
  Partial<Props> & { stripeId?: string; as?: Props['as']; dataType?: 'customers' | 'invoices' }
> = ({ stripeId, dataType = 'customers', as = 'href', ...props }) => {
  if (stripeId == null) return <></>;
  const isTestEnvironment =
    window.location.hostname === 'localhost' || window.location.hostname === 'gears.racemap.com';

  return (
    <RawLink
      link={`https://dashboard.stripe.com/${isTestEnvironment ? 'test/' : ''}customers/${stripeId}`}
      label="Dashboard"
      title={`Open the ${dataType} dashboard in stripe!`}
      icon={<IconStripe />}
      as={as}
      {...props}
    />
  );
};

export const BillingLink: FC<
  Partial<Props> & { userId: string | ObjectId; as?: Props['as']; label?: string }
> = ({ userId, as = 'href', label = 'Billing', ...props }) => {
  return (
    <RawLink
      link={`/admin/account/${userId.toString()}/billing`}
      label={label}
      title="Show the billing page."
      icon={<IconWallet />}
      as={as}
      {...props}
    />
  );
};

export const AccountLink: FC<
  Partial<Props> & { userId: string | ObjectId; as?: Props['as']; label?: string }
> = ({ userId, as = 'href', ...props }) => {
  return (
    <RawLink
      link={`/admin/account/${userId.toString()}`}
      label={props.label ?? 'Account'}
      title="Show the account of the user!"
      icon={<IconUser />}
      as={as}
      {...props}
    />
  );
};

export const UserMapsLink: FC<Partial<Props> & { userId: string | ObjectId; as?: Props['as'] }> = ({
  userId,
  as = 'href',
  ...props
}) => {
  return (
    <RawLink
      link={`/admin/events/manage?userId=${userId.toString()}&searchBy=USER`}
      label="User Maps"
      title="Show the maps of the user!"
      icon={<IconMap />}
      as={as}
      {...props}
    />
  );
};

export const DocuLink: FC<Partial<Props> & { path: string; as?: Props['as'] }> = ({
  path,
  as = 'href',
  ...props
}) => {
  return (
    <RawLink
      link={`https://docs.racemap.com/${path}`}
      label="Documentation"
      title="Open the documentation in a new tab!"
      icon={<IconBook />}
      as={as}
      {...props}
    />
  );
};

export const MapEditorLink: FC<
  Partial<Props> & {
    eventId: string;
    geoElementType?: RacemapGeoTypes;
    geoElementId?: string;
    as?: Props['as'];
  }
> = ({ eventId, geoElementType, geoElementId, as = 'href', ...props }) => {
  let link = `/admin/events/${eventId}/map`;
  if (geoElementType != null) {
    link += `/${geoElementType}`;
    if (geoElementId != null) {
      link += `/${geoElementId}`;
    }
  }

  return (
    <RawLink
      link={link}
      label="Map Editor"
      title="Open the map editor!"
      icon={<IconMap />}
      as={as}
      {...props}
    />
  );
};

export const ParticipantListLink: FC<
  Partial<Props> & {
    eventId: string;
    tab?: ParticipantTabKeys;
    as?: Props['as'];
  }
> = ({ eventId, tab = 'list', as = 'href', ...props }) => {
  let link = `/admin/events/${eventId}/participants`;
  if (tab != null) {
    link += `/${tab}`;
  }

  return (
    <RawLink
      link={link}
      label="Participant List"
      title="Open the participant list!"
      icon={<IconRunner />}
      as={as}
      {...props}
    />
  );
};

export const PredictionTabLink: FC<
  Partial<Props> & {
    eventId: string;
    tab?: PredictionTabKeys;
    as?: Props['as'];
  }
> = ({ eventId, tab = 'list', as = 'href', ...props }) => {
  let link = `/admin/events/${eventId}/prediction`;
  if (tab != null) {
    link += `/${tab}`;
  }

  return (
    <RawLink
      link={link}
      label="Prediction"
      title="Open the prediction tab!"
      icon={<IconPrediction />}
      as={as}
      {...props}
    />
  );
};

export const AnalyticsTabLink: FC<
  Partial<Props> & {
    eventId: string;
    as?: Props['as'];
  }
> = ({ eventId, as = 'href', ...props }) => {
  const link = `/admin/events/${eventId}/analytics`;

  return (
    <RawLink
      link={link}
      label="Analytics"
      title="Open the analytics tab!"
      icon={<IconChartLine />}
      as={as}
      {...props}
    />
  );
};

export const CostCalculatorLink: FC<
  Partial<Props> & { product?: 'events' | 'tracker-buy' | 'tracker-rent'; as?: Props['as'] }
> = ({ product = 'events', as = 'href', ...props }) => {
  return (
    <RawLink
      link={`/cost-calculator/${product}`}
      label="Cost Calculator"
      title="Show the cost calculator!"
      icon={<IconCalculator />}
      as={as}
      {...props}
    />
  );
};

export const PricingLink: FC<Partial<Props> & { as?: Props['as'] }> = ({
  as = 'href',
  ...props
}) => {
  return (
    <RawLink
      link="https://racemap.com/pricing"
      label="Documentation"
      title="Open the documentation in a new tab!"
      icon={<IconMoney />}
      as={as}
      {...props}
    />
  );
};

export const ExternalLink: FC<
  Partial<Props> & { as?: Props['as']; link: Props['link']; label: Props['label'] }
> = ({ as = 'href', ...props }) => {
  return <RawLink as={as} {...props} icon={props.icon || <IconExternal />} />;
};

export const VisualisationLink: FC<Partial<Props> & { as?: Props['as']; eventId: string }> = ({
  as = 'href',
  eventId,
  ...props
}) => {
  return (
    <RawLink
      link={`/admin/events/${eventId}/visualisation`}
      label="Visualisation"
      title="Open the visualisation tab!"
      icon={<IconPaintRoller />}
      as={as}
      {...props}
    />
  );
};

export const ImportExportParticipantsLink: FC<
  Partial<Props> & { as?: Props['as']; eventId: string }
> = ({ as = 'href', eventId, ...props }) => {
  return (
    <RawLink
      link={`/admin/events/${eventId}/participants/import_export`}
      label="Import/Export"
      title="Open the import/export tab!"
      icon={<IconRunner />}
      as={as}
      {...props}
    />
  );
};

export const LeaderboardLink: FC<Partial<Props> & { as?: Props['as']; eventId: string }> = ({
  as = 'href',
  eventId,
  ...props
}) => {
  return (
    <RawLink
      link={`/admin/events/${eventId}/leaderboard`}
      label="Leaderboard"
      title="Open the leaderboard tab!"
      icon={<IconLeaderboard />}
      as={as}
      {...props}
    />
  );
};

export const IntegrationsLink: FC<Partial<Props> & { as?: Props['as']; eventId: string }> = ({
  as = 'href',
  eventId,
  ...props
}) => {
  return (
    <RawLink
      link={`/admin/events/${eventId}/integrations`}
      label="Integrations"
      title="Open the integrations tab!"
      icon={<IconLink />}
      as={as}
      {...props}
    />
  );
};

export const TrackerManagementLink: FC<Partial<Props> & { as?: Props['as'] }> = ({
  as = 'href',
  ...props
}) => {
  return (
    <RawLink
      link={'/admin/trackers'}
      label="Tracker Management"
      title="Open the tracker management tab!"
      icon={<IconTracker />}
      as={as}
      {...props}
    />
  );
};
